// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import translate from "@insite/client-framework/Translate";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Link } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import CartLineContainer from "@sc/App/Components/Cart/CartLineContainer";
import { hidePrice } from "@sc/App/Store/Selectors/GeneralSelectors";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    extendedStyles?: SavedOrderDetailsCartLineStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    allowMultipleWishLists: getSettingsCollection(state).wishListSettings.allowMultipleWishLists,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    // XNG-Change
    hidePrices: hidePrice(state),
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderDetailsCartLineStyles {
    productPrice?: ProductPriceStyles;
    cartLineQuantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    savedOrderLineInfoGridItem?: GridItemProps;
    savedOrderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    productAvailabilityGridItem?: GridItemProps;
    productAvailability?: ProductAvailabilityStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    inactiveProductLabel?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    quantityGridItem?: GridItemProps;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    savedOrderLineCardAddToListGridItem?: GridItemProps;
    savedOrderLineCardAddToListButton?: ButtonPresentationProps;
    // XNG-Changes
    savedOrderLineCardAddToListLink?: LinkPresentationProps;
    noPrice?: SmallHeadingAndTextStyles;
}

export const savedOrderDetailsCartLineStyles: SavedOrderDetailsCartLineStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: [2, 2, 2, 1, 1],
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    savedOrderLineInfoGridItem: {
        width: [10, 10, 10, 11, 11],
        printWidth: 11,
    },
    savedOrderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    productAvailabilityGridItem: {
        width: 12,
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 7, 7, 7],
        printWidth: 7,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
    },
    priceGridItem: {
        width: [12, 12, 12, 4, 4],
        printWidth: 4,
        css: css`
            flex-direction: column;
        `,
    },
    productPrice: {
        price: {
            priceText: {
                weight: "normal",
            },
        },
        secondaryPrice: {
            priceText: {
                weight: "normal",
            },
        },
    },
    inactiveProductLabel: {
        color: "danger",
    },
    quantityGridItem: {
        width: [2, 2, 2, 1, 1],
    },
    extendedUnitNetPriceGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
    subtotalWithoutVatText: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    vatAmount: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    savedOrderLineCardAddToListGridItem: {
        width: [6, 6, 6, 4, 4],
        printWidth: 0,
    },
};

const SavedOrderDetailsCartLine: React.FC<Props> = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
    allowMultipleWishLists,
    enableVat,
    vatPriceDisplay,
    showPricing,
    setAddToListModalIsOpen,
    addToWishList,
    toaster,
    // XNG-Change
    hidePrices,
}) => {
    const [styles] = React.useState(() => mergeToNew(savedOrderDetailsCartLineStyles, extendedStyles));

    const addToListClickHandler = () => {
        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure ?? "",
        };

        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toaster.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toaster.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    // XNG-Changes: Structure
    return (
        <CartLineContainer data-test-selector={`savedOrderDetails_orderLine_${cartLine.id}`}>
            <CartLineContainer.Image>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                <ProductPartNumbers
                    productNumber={cartLine.erpNumber}
                    customerProductNumber={cartLine.customerName}
                    manufacturerItem={cartLine.manufacturerItem}
                    extendedStyles={styles.productPartNumbers}
                />
                {cartLine.isRestricted && (
                    <GridItem {...styles.productAvailabilityGridItem}>
                        <Typography {...styles.cartLineErrorMessageText}>{translate("Restricted product")}</Typography>
                    </GridItem>
                )}
                {!cartLine.isActive && (
                    <Typography {...styles.inactiveProductLabel} data-test-selector="savedOrderDetails_inactiveProduct">
                        {translate("Inactive Product")}
                    </Typography>
                )}
            </CartLineContainer.Description>
            <CartLineContainer.Price>
                <ProductPrice
                    product={cartLine}
                    showSavings={true}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    extendedStyles={styles.productPrice}
                    showLabel={true}
                    label="Price/Unit"
                />

                <ProductAvailability
                    productId={cartLine.productId!}
                    availability={cartLine.availability!}
                    unitOfMeasure={cartLine.unitOfMeasure}
                    trackInventory={cartLine.trackInventory}
                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                    extendedStyles={styles.productAvailability}
                    {...{
                        showOutOfStockText: true,
                        isCartPage: true,
                        totalAvailableQty: cartLine.qtyOnHand,
                        inBranchQty: Number(cartLine.properties?.qtyInBranch || 0),
                    }}
                />
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity cart={cart} editable={false} extendedStyles={styles.cartLineQuantity} />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {cartLine.pricing && showPricing && (
                    <>
                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <>
                                <SmallHeadingAndText
                                    heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                    text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                    extendedStyles={styles.subtotalWithoutVatText}
                                />
                                <SmallHeadingAndText
                                    heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                    text={cartLine.pricing.vatAmountDisplay}
                                    extendedStyles={styles.vatAmount}
                                />
                            </>
                        )}
                        <SmallHeadingAndText
                            // XNG-Change
                            {...(hidePrices ? { ...styles.noPrice } : { ...styles.extendedUnitNetPrice })}
                            heading={
                                !enableVat
                                    ? translate("Total")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                    : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                            }
                            text={
                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : cartLine.pricing.extendedUnitNetPriceDisplay
                            }
                        />
                    </>
                )}
            </CartLineContainer.Total>
            {/* XNG-Changes: adding  */}
            <CartLineContainer.Notes>
                {cartLine.canAddToWishlist && !cartLine.isRestricted && (
                    <Link
                        {...styles.savedOrderLineCardAddToListLink}
                        data-test-selector={`savedOrderDetails_productLine_${cartLine.erpNumber}`}
                        onClick={addToListClickHandler}
                        aria-label="Add to List"
                    >
                        {translate("Add to List")}
                    </Link>
                )}
            </CartLineContainer.Notes>
            {/* <CartLineContainer.Remove>
                {cartLine.canAddToWishlist && !cartLine.isRestricted && (
                    <Link
                        {...styles.savedOrderLineCardAddToListLink}
                        data-test-selector={`savedOrderDetails_productLine_${cartLine.erpNumber}`}
                        onClick={addToListClickHandler}
                        aria-label="Add to List"
                    >
                        {translate("Add to List")}
                    </Link>
                )}
            </CartLineContainer.Remove> */}
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withCartLine(SavedOrderDetailsCartLine)));
